import React, { useState, useEffect } from "react";
import { Table, Typography, Tag, Space, Input, Badge, Tooltip, Button } from "antd";
import { Link } from "react-router-dom";
import { SearchOutlined, PlusOutlined } from "@ant-design/icons";
import axios from "axios";
import { useAuth0 } from "../../react-auth0-spa";
import { useAppContext } from "../../context";
import config from "../../api_config.json";
import sellersQuery from "./queries/sellersQuery";
import allOrgsWithSellersQuery from "./queries/allOrgsWithSellersQuery";
import ParseLogo from "../shared/images/parse.png";
import CreateSellerModal from "./CreateSellerModal";

const { Title } = Typography;
const { Search } = Input;

const MarketplaceSellers = () => {
  const [loading, setLoading] = useState(true);
  const [sellers, setSellers] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [sellerToOrgMap, setSellerToOrgMap] = useState({});
  const [mapLoading, setMapLoading] = useState(true);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  const { getTokenSilently } = useAuth0();
  const { isProd } = useAppContext();
  const env = isProd ? "prod" : "dev";

  const fetchSellers = async (page = 1, pageSize = 20, search = "") => {
    setLoading(true);
    try {
      const token = await getTokenSilently();
      const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;

      const where = {};
      if (search) {
        where.OR = [
          { name: { matchesRegex: search, options: "i" } },
          { phone: { matchesRegex: search, options: "i" } },
        ];
      }

      const variables = {
        first: pageSize,
        skip: (page - 1) * pageSize,
        where: Object.keys(where).length > 0 ? where : undefined,
        order: ["createdAt_DESC"],
      };

      const response = await axios.post(
        graphqlUrl,
        {
          query: sellersQuery,
          variables,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const edges = response.data?.marketplace_sellers?.edges || [];
      const count = response.data?.marketplace_sellers?.count || 0;

      setSellers(edges.map((edge) => edge.node));
      setPagination((prev) => ({ ...prev, current: page, pageSize, total: count }));
    } catch (error) {
      console.error("Error fetching marketplace sellers:", error);
      setSellers([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch orgs with seller links to build reverse lookup map
  // Limited to 2000 to prevent performance issues with large datasets
  const fetchSellerToOrgMap = async () => {
    setMapLoading(true);
    try {
      const token = await getTokenSilently();
      const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;

      const response = await axios.post(
        graphqlUrl,
        {
          query: allOrgsWithSellersQuery,
          variables: { first: 2000 },
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const edges = response.data?.marketplace_orgs?.edges || [];
      const count = response.data?.marketplace_orgs?.count || 0;

      // Log warning if we're hitting the limit
      if (count > 2000) {
        console.warn(`Total orgs with sellers (${count}) exceeds fetch limit (2000). Some links may not be displayed.`);
      }

      const map = {};
      edges.forEach((edge) => {
        const org = edge.node;
        if (org.marketplace_seller_pointer?.objectId) {
          map[org.marketplace_seller_pointer.objectId] = org;
        }
      });
      setSellerToOrgMap(map);
    } catch (error) {
      console.error("Error fetching seller to org map:", error);
    } finally {
      setMapLoading(false);
    }
  };

  useEffect(() => {
    fetchSellerToOrgMap();
    fetchSellers(pagination.current, pagination.pageSize, searchValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTableChange = (newPagination) => {
    fetchSellers(newPagination.current, newPagination.pageSize, searchValue);
  };

  const handleSearch = (value) => {
    setSearchValue(value);
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchSellers(1, pagination.pageSize, value);
  };

  const handleCreateSeller = () => {
    setCreateModalVisible(false);
    // Refresh the list after creating
    fetchSellers(1, pagination.pageSize, searchValue);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "objectId",
      key: "objectId",
      render: (id) => <Link to={`/funeralcloud/sellers/${id}`}>{id}</Link>,
    },
    {
      title: "Nome",
      dataIndex: "name",
      key: "name",
      render: (name, record) => (
        <Link to={`/funeralcloud/sellers/${record.objectId}`}>{name}</Link>
      ),
    },
    {
      title: "Telefono",
      dataIndex: "phone",
      key: "phone",
    },
    {
      title: "Organizzazione FuneralCloud",
      key: "marketplace_org",
      render: (_, record) => {
        if (mapLoading) {
          return <span style={{ color: "#999" }}>Caricamento...</span>;
        }
        const org = sellerToOrgMap[record.objectId];
        return org ? (
          <Link to={`/funeralcloud/orgs/${org.objectId}/riepilogo`}>
            {org.name}
          </Link>
        ) : (
          "-"
        );
      },
    },
    {
      title: "Organizzazione Eterno",
      dataIndex: ["org_pointer", "nome"],
      key: "org",
      render: (nome, record) =>
        record.org_pointer ? (
          <Link to={`/orgs/${record.org_pointer.objectId}`}>{nome}</Link>
        ) : (
          "-"
        ),
    },
    {
      title: "Stato",
      dataIndex: "enabled",
      key: "enabled",
      render: (enabled) => (
        <Tag color={enabled ? "green" : "red"}>
          {enabled ? "ATTIVO" : "DISATTIVO"}
        </Tag>
      ),
      filters: [
        { text: "Attivo", value: true },
        { text: "Disattivo", value: false },
      ],
      onFilter: (value, record) => record.enabled === value,
    },
    {
      title: "Pubblico",
      dataIndex: "public",
      key: "public",
      render: (isPublic) => (
        <Tag color={isPublic ? "blue" : "default"}>
          {isPublic ? "SI" : "NO"}
        </Tag>
      ),
    },
    {
      title: "B2B",
      dataIndex: "b2b",
      key: "b2b",
      render: (b2b) => (
        <Tag color={b2b ? "purple" : "default"}>
          {b2b ? "SI" : "NO"}
        </Tag>
      ),
    },
    {
      title: "B2C",
      dataIndex: "b2c",
      key: "b2c",
      render: (b2c) => (
        <Tag color={b2c ? "purple" : "default"}>
          {b2c ? "SI" : "NO"}
        </Tag>
      ),
    },
    {
      title: "Links",
      key: "links",
      render: (_, record) => {
        const parseDashboardUrl = `${config.parseDashboard[isProd ? "prod" : "dev"]}/browser/marketplace_seller?filters=[{"field":"objectId","constraint":"eq","compareTo":"${record.objectId}"}]`;
        return (
          <Tooltip title="Visualizza in Parse Dashboard" placement="top">
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={parseDashboardUrl}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                borderRadius: '6px',
                backgroundColor: 'transparent',
                transition: 'all 0.2s ease',
              }}
            >
              <img
                src={ParseLogo}
                width={20}
                alt="Parse Dashboard"
                style={{ display: 'block' }}
              />
            </a>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <div style={{ margin: "16px 16px" }}>
      <div style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: "100%" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Title level={3} style={{ margin: 0 }}>
              <Space>
                Venditori Marketplace
                <Badge overflowCount={5000} count={pagination.total} />
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setCreateModalVisible(true)}
                >
                  Crea Venditore
                </Button>
              </Space>
            </Title>
            <Search
              placeholder="Cerca per nome o telefono"
              allowClear
              enterButton={<SearchOutlined />}
              size="large"
              onSearch={handleSearch}
              style={{ width: 400 }}
            />
          </div>
        </Space>
      </div>

      <Table
        dataSource={sellers}
        columns={columns}
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        rowKey="objectId"
        scroll={{ x: "max-content" }}
      />

      <CreateSellerModal
        visible={createModalVisible}
        onCancel={() => setCreateModalVisible(false)}
        onCreate={handleCreateSeller}
      />
    </div>
  );
};

export default MarketplaceSellers;
